import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  PlusIcon,
  MagnifyingGlassIcon,
  FolderIcon,
  ClockIcon,
  Bars3Icon,
  TrashIcon,
  ChevronDownIcon,
  ArrowsUpDownIcon,
  Cog6ToothIcon,
} from "@heroicons/react/24/outline";
import { useNotes } from "../context/NotesContext";
import { useError } from "../context/ErrorContext";
import { useClickOutside } from "../hooks/useClickOutside";
import ConfirmModal from "../components/ConfirmModal";
import FolderManageModal from "../components/FolderManageModal";
import Sidebar from "../components/Sidebar";
import FolderSelector from "../components/FolderSelector";
import HamburgerButton from "../components/HamburgerButton";

const sortOptions = [
  { value: "date", label: "Date Modified" },
  { value: "title", label: "Title" },
  { value: "folder", label: "Folder" },
];

const Notes = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showError } = useError();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFolder, setSelectedFolder] = useState("All Notes");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const isCollapsed = localStorage.getItem("sidebar_collapsed") === "true";
  const {
    notes,
    createNote,
    deleteNote,
    updateNote,
    folders,
    addFolder,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
  } = useNotes();
  const [showSortMenu, setShowSortMenu] = useState(false);
  const sortMenuRef = useClickOutside(() => setShowSortMenu(false));
  const [deleteNoteId, setDeleteNoteId] = useState(null);
  const [showFolderManage, setShowFolderManage] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);
  const [activeFolderMenu, setActiveFolderMenu] = useState(null);

  const getFolderCount = useCallback(
    (folder) => {
      if (folder === "All Notes") {
        return notes.length;
      }
      return notes.filter((note) => note.folder === folder).length;
    },
    [notes]
  );

  const handleSearch = useCallback((e) => {
    setSearchQuery(e.target.value);
  }, []);

  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen((prev) => !prev);
  }, []);

  const handleNewNote = useCallback(
    async (folder = "All Notes") => {
      try {
        console.log("Creating new note in folder:", folder);
        const newNote = await createNote({
          title: "Untitled",
          content: "",
          folder: folder === "All Notes" ? "" : folder,
          keyPoints: [],
          userId: localStorage.getItem("userId"),
        });

        console.log("Response from createNote:", newNote);

        if (newNote && newNote._id) {
          navigate(`/notes/${newNote._id}`);
        } else {
          throw new Error("Failed to create note - no ID returned");
        }
      } catch (error) {
        console.error("Error creating note:", error);
        showError("Failed to create note");
      }
    },
    [createNote, navigate, showError]
  );

  const handleCreateFolder = useCallback(
    (folderName) => {
      if (addFolder(folderName)) {
        setShowFolderManage(false);
        setSelectedFolder(folderName);
      }
    },
    [addFolder]
  );

  const handleEditNote = useCallback(
    (note) => {
      navigate(`/notes/${note._id}`);
    },
    [navigate]
  );

  const handleDeleteNote = useCallback(
    (id) => {
      deleteNote(id);
      setDeleteNoteId(null);
    },
    [deleteNote]
  );

  const handleCreateNewFolder = (e) => {
    e.preventDefault();
    if (!newFolderName.trim()) {
      showError("Please enter a folder name");
      return;
    }

    if (folders.includes(newFolderName.trim())) {
      showError("Folder already exists");
      return;
    }

    if (addFolder(newFolderName.trim())) {
      setNewFolderName("");
      setShowNewFolderInput(false);
      setSelectedFolder(newFolderName.trim());
    }
  };

  // Filter notes based on selected folder and search query
  const filteredNotes = notes
    .filter((note) => {
      console.log("Filtering note:", {
        note,
        selectedFolder,
        noteFolder: note.folder,
        isMatch:
          selectedFolder === "All Notes" || note.folder === selectedFolder,
      });

      // If "All Notes" is selected, show all notes
      if (selectedFolder === "All Notes") {
        return true;
      }

      // For other folders, match exactly with the note's folder
      const matchesFolder = note.folder === selectedFolder;

      // Search filtering remains the same
      const matchesSearch =
        note.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        note.content.toLowerCase().includes(searchQuery.toLowerCase());

      return matchesFolder && matchesSearch;
    })
    .sort((a, b) => {
      if (sortBy === "title") {
        // Sort alphabetically by title (reversed the sortOrder logic)
        return sortOrder === "desc"
          ? a.title.localeCompare(b.title) // Show A-Z when descending
          : b.title.localeCompare(a.title); // Show Z-A when ascending
      } else if (sortBy === "folder") {
        // Sort alphabetically by folder (reversed the sortOrder logic)
        const folderA = a.folder || "All Notes";
        const folderB = b.folder || "All Notes";
        return sortOrder === "desc"
          ? folderA.localeCompare(folderB) // Show A-Z when descending
          : folderB.localeCompare(folderA); // Show Z-A when ascending
      } else {
        // Sort by date (keep this the same)
        return sortOrder === "asc"
          ? new Date(a.updatedAt) - new Date(b.updatedAt)
          : new Date(b.updatedAt) - new Date(a.updatedAt);
      }
    });

  // Add useEffect to close sidebar on route change
  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  // Add useEffect to close sidebar on window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        // lg breakpoint
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Add a click outside handler to close the folder menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeFolderMenu && !event.target.closest(".folder-menu")) {
        setActiveFolderMenu(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [activeFolderMenu]);

  return (
    <div className="min-h-screen bg-dark-bg">
      <Sidebar isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      <HamburgerButton onClick={toggleSidebar} isOpen={isSidebarOpen} />
      <main
        className={`transition-all duration-500 ease-in-out ${"ml-0 lg:ml-64"}`}
      >
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-4">
          {/* Header */}
          <div className="mb-6">
            {/* Title */}
            <h1 className="text-3xl font-bold text-white mb-4">Notes</h1>

            {/* New Note button on its own line */}
            <button
              onClick={() => {
                console.log("New Note button clicked");
                console.log("Selected folder:", selectedFolder);
                handleNewNote(selectedFolder);
              }}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              <PlusIcon className="w-5 h-5" />
              <span>New Note</span>
            </button>
          </div>

          {/* Search and Sort Bar */}
          <div className="flex flex-col sm:flex-row gap-4 mb-8">
            {/* Search */}
            <div className="relative flex-1">
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearch}
                placeholder="Search notes..."
                className="w-full bg-dark-card text-gray-200 pl-10 pr-4 py-2.5 rounded-lg border border-gray-800 focus:outline-none focus:border-blue-500 transition-colors"
              />
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            </div>

            {/* Sort Dropdown */}
            <div className="relative" ref={sortMenuRef}>
              <button
                onClick={() => setShowSortMenu(!showSortMenu)}
                className="w-full sm:w-auto flex items-center justify-between px-4 py-2.5 bg-dark-card text-gray-400 rounded-lg hover:text-white hover:bg-dark-hover transition-colors min-w-[160px]"
              >
                <div className="flex items-center">
                  <ArrowsUpDownIcon className="w-5 h-5 mr-2" />
                  <span>
                    Sort by:{" "}
                    {sortOptions.find((opt) => opt.value === sortBy)?.label}
                  </span>
                </div>
                <ChevronDownIcon className="w-5 h-5 ml-2" />
              </button>

              {/* Sort Menu */}
              {showSortMenu && (
                <div className="absolute top-full right-0 mt-2 w-48 bg-dark-card rounded-lg shadow-lg border border-gray-800 py-1 z-10">
                  {sortOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        if (sortBy === option.value) {
                          setSortOrder((current) =>
                            current === "asc" ? "desc" : "asc"
                          );
                        } else {
                          setSortBy(option.value);
                          setSortOrder("desc");
                        }
                        setShowSortMenu(false);
                      }}
                      className="w-full text-left px-4 py-2 text-base text-gray-400 hover:text-white hover:bg-dark-hover transition-colors flex items-center justify-between"
                    >
                      <span>{option.label}</span>
                      {sortBy === option.value && (
                        <span className="text-blue-500">
                          {sortOrder === "asc" ? "↑" : "↓"}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Folders Grid */}
          <div className="flex flex-wrap gap-2 mb-6">
            {/* Manage Folders button */}
            <button
              onClick={() => setShowFolderManage(true)}
              className="w-10 h-10 rounded-lg bg-dark-card text-gray-400 hover:bg-dark-hover hover:text-white transition-colors flex items-center justify-center"
              title="Manage Folders"
            >
              <Cog6ToothIcon className="w-4 h-4" />
            </button>

            {/* All Notes button */}
            <button
              onClick={() => setSelectedFolder("All Notes")}
              className={`h-10 px-3 rounded-lg flex items-center justify-between min-w-[120px] ${
                selectedFolder === "All Notes"
                  ? "bg-blue-500 text-white"
                  : "bg-dark-card text-gray-400 hover:bg-dark-hover hover:text-white"
              } transition-colors`}
            >
              <div className="flex items-center">
                <FolderIcon className="w-4 h-4 mr-2" />
                <span className="text-sm truncate">All Notes</span>
              </div>
              <span className="text-xs ml-2">
                {getFolderCount("All Notes")}
              </span>
            </button>

            {/* User Created Folders */}
            {folders
              .filter(
                (folder) =>
                  folder !== "All Notes" &&
                  folder !== "Default" &&
                  folder !== ""
              )
              .map((folder) => (
                <button
                  key={folder}
                  onClick={() => setSelectedFolder(folder)}
                  className={`h-10 px-3 rounded-lg flex items-center justify-between min-w-[120px] ${
                    selectedFolder === folder
                      ? "bg-blue-500 text-white"
                      : "bg-dark-card text-gray-400 hover:bg-dark-hover hover:text-white"
                  } transition-colors`}
                >
                  <div className="flex items-center">
                    <FolderIcon className="w-4 h-4 mr-2" />
                    <span className="text-sm truncate">{folder}</span>
                  </div>
                  <span className="text-xs ml-2">{getFolderCount(folder)}</span>
                </button>
              ))}
          </div>

          {/* Note Cards Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {filteredNotes.map((note) => (
              <div
                key={note._id}
                className="group bg-dark-card rounded-lg p-4 sm:p-6 hover:bg-dark-hover transition-all duration-200 cursor-pointer relative"
                onClick={() => handleEditNote(note)}
              >
                <div className="flex flex-col h-full">
                  <div className="flex items-start justify-between mb-4">
                    <h3 className="text-base sm:text-lg font-medium text-white truncate pr-4">
                      {note.title || "Untitled"}
                    </h3>

                    <div
                      className="flex items-center space-x-2 group-hover:opacity-100 transition-opacity absolute top-4 right-4 bg-dark-hover/80 backdrop-blur-sm rounded-lg p-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* Add Folder Menu */}
                      <div className="relative">
                        <FolderSelector
                          folders={folders}
                          currentFolder={note.folder}
                          onSelect={async (selectedFolder) => {
                            try {
                              const newFolder =
                                selectedFolder === "All Notes"
                                  ? ""
                                  : selectedFolder;
                              console.log("Folder selected:", {
                                selectedFolder,
                                newFolder,
                                noteId: note._id,
                              });

                              const updatedNoteData = {
                                ...note,
                                folder: newFolder,
                              };

                              await updateNote(note._id, updatedNoteData);
                              setActiveFolderMenu(null);
                            } catch (error) {
                              console.error("Failed to update folder:", error);
                              showError("Failed to move note to folder");
                            }
                          }}
                          isOpen={activeFolderMenu === note._id}
                          onClose={() => setActiveFolderMenu(null)}
                        />
                      </div>

                      {/* Existing Delete Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteNoteId(note._id);
                        }}
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <p className="text-gray-400 text-sm line-clamp-3 mb-4 flex-grow">
                    {note.content || "No content"}
                  </p>

                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <div className="flex items-center">
                      <FolderIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                      <span className="truncate">
                        {note.folder || "All Notes"}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <ClockIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                      <span>
                        {new Date(note.updatedAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* Folder Management Modal */}
      {showFolderManage && (
        <FolderManageModal onClose={() => setShowFolderManage(false)} />
      )}

      {/* Other modals */}
      <ConfirmModal
        isOpen={deleteNoteId !== null}
        onClose={() => setDeleteNoteId(null)}
        onConfirm={() => handleDeleteNote(deleteNoteId)}
        title="Delete Note"
        message="Are you sure you want to delete this note? This action cannot be undone."
      />

      {/* Add overlay for mobile */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/30 backdrop-blur-sm z-30 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default Notes;
